import _ from "lodash";
import React, { useState } from "react";
import { connect } from "react-redux";

import { Button, Modal, TextInput } from "@toddle-edu/ds-web";
import { useI18n } from "Hooks";

import {
  cloneAssessment,
  deleteClonedAssessment,
  updateAssessment,
} from "Assessments/modules/AssessmentModule";
import { getUnitPlanIdFromAssessment } from "Assessments/modules/Utils";
import {
  createAssignment,
  getAssignmentStateTabs,
  updateClassRoomFilters,
} from "ClassRoom/modules/ClassRoomModule";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import TruncatedTextWithTooltip from "UIComponents/TruncatedTextWithTooltip";

import classes from "./DuplicateAssignmentDialog.scss";

const MODAL_DIMENSIONS = {
  width: 480,
  height: "fit-content",
};

const DuplicateAssignmentDialog = ({
  assignment,
  course,
  courseId,
  learningCourseId,
  onClose,
  cloneAssessment,
  deleteClonedAssessment,
  updateAssessment,
  createAssignment,
  updateClassRoomFilters,
  setToastMsg,
}) => {
  const { t } = useI18n(["common", "toastMsgs"]);

  const originalTitle =
    _.get(assignment, "content.title.value", "") ||
    _.get(assignment, "content.assignmentTitle", "") ||
    "";

  const [title, setTitle] = useState(
    t("common:label_copy_with_brackets", { label: originalTitle })
  );
  const [isLoading, setIsLoading] = useState(false);

  const isTitleEmpty = _.isEmpty(_.trim(title));

  const handleDuplicate = async () => {
    setIsLoading(true);

    let clonedAssessmentId = null;

    try {
      // Step 1: Clone the assessment
      const cloneResult = await cloneAssessment({
        sourceAssessmentId: _.get(assignment, "content.id", null),
        portalType: "PLANNER",
        academicYears: _.map(
          _.get(course, "academicYears", []),
          ({ id }) => id
        ),
        unitPlanId: getUnitPlanIdFromAssessment({
          assessmentDetails: _.get(assignment, "content", {}),
        }),
        learningCourseId,
      });
      // cloneAssessment returns `false` on mutation error (after dispatching
      // its own toast); otherwise an object. Either way, an empty
      // clonedAssessmentId means step 1 didn't produce a usable clone,
      // and the user has already seen a toast from the thunk.
      clonedAssessmentId = _.get(cloneResult, "clonedAssessmentId", null);
      const clonedTitleFieldId = _.get(cloneResult, "clonedTitleFieldId");

      if (_.isEmpty(clonedAssessmentId)) {
        setIsLoading(false);
        return;
      }

      // Step 2: Update the cloned assessment's title using its own
      // title field id (the source assessment's field id would update
      // the source instead of the clone).
      if (!clonedTitleFieldId) {
        throw new Error("Cloned assessment is missing title field id");
      }
      await updateAssessment({
        assessmentId: clonedAssessmentId,
        fields: [{ id: clonedTitleFieldId, value: _.trim(title) }],
      });

      // Step 3: Create draft assignment with the cloned assessment
      const contentType = _.get(assignment, "contentType", "ASSESSMENT");
      const currentCourse = {
        courseId: courseId || _.get(course, "id"),
        title: _.get(course, "title", ""),
      };

      await createAssignment({
        leId: clonedAssessmentId,
        contentType,
        courses: [currentCourse],
        state: "DRAFT",
        creationMode: "CREATE_ASSESSMENT",
        creationSource: "CLASSROOM",
        creationSubSource: "ASSIGNMENT_DUPLICATE",
        isDuplicateAssignment: true,
      });

      const assignmentStateTabs = getAssignmentStateTabs();
      const draftTab = _.get(assignmentStateTabs, "DRAFT");
      if (draftTab) {
        updateClassRoomFilters({
          assignmentStateTab: draftTab.value,
          folderTopic: "ALL",
          status: "ALL",
          orderBy: _.get(draftTab, "orderBy", ""),
        });
      }

      setToastMsg({
        type: "success",
        msg: "toastMsgs:entity_duplicated_successfully",
        locale_params: [
          { key: "entity", value: "common:assignment", isPlainText: false },
        ],
      });

      onClose(true);
    } catch (err) {
      // Roll back the orphaned clone so it doesn't pile up in the
      // backend when step 2 or 3 fails.
      if (clonedAssessmentId) {
        await deleteClonedAssessment({
          assessmentId: clonedAssessmentId,
          portalType: "PLANNER",
        });
      }
      setToastMsg("toastMsgs:something_went_wrong");
      setIsLoading(false);
    }
  };

  return (
    <>
      <Modal
        {...MODAL_DIMENSIONS}
        dsVersion="2.0"
        isOpen={true}
        onClose={isLoading ? undefined : () => onClose(false)}
      >
        <Modal.Header
          isClosable={!isLoading}
          testId="duplicate-assignment-dialog"
        >
          <TruncatedTextWithTooltip
            title={t("common:duplicate_with_quoted_label", {
              label: _.trim(originalTitle),
            })}
          />
        </Modal.Header>
        <Modal.Body isScrollable={false}>
          <div className={classes.modalBody}>
            <TextInput
              label={t("common:title")}
              value={title}
              onChange={e => setTitle(e.target.value)}
              testId="duplicate-assignment-dialog-title-input"
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className={classes.modalFooter}>
            <Button
              onClick={() => onClose(false)}
              size="medium"
              variant="neutral"
              type="outlined"
              testId="duplicate-assignment-dialog-cancel"
            >
              {t("common:cancel")}
            </Button>
            <Button
              variant="progressive"
              size="medium"
              type="fill"
              onClick={handleDuplicate}
              shortcut={{ code: "Enter", shiftKey: true }}
              disabled={isTitleEmpty}
              testId="duplicate-assignment-dialog-confirm"
            >
              {t("common:duplicate")}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
      {isLoading ? <FullScreenLoader /> : null}
    </>
  );
};

const mapActionCreators = {
  cloneAssessment,
  deleteClonedAssessment,
  updateAssessment,
  createAssignment,
  updateClassRoomFilters,
  setToastMsg,
};

export default connect(null, mapActionCreators)(DuplicateAssignmentDialog);
